'use client'

import { XIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import type * as React from 'react'

import { useDocumentDirection } from '../../hooks/use-document-direction'
import { cn } from '../../lib/utils'
import { Button } from './button'

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        // The scrim token, flat, with no backdrop blur: a blur reads as soft
        // and costs a compositor layer over a data grid.
        'fixed inset-0 z-50 bg-scrim',
        'duration-[var(--motion-base)] data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  )
}

/**
 * `closeLabel` is required, not defaulted. The close button is the one control
 * this package renders that the caller did not write the text for, and a default
 * of "Close" would be an English string shipped inside a bilingual component —
 * invisible to review because it only surfaces to a screen reader.
 */
function DialogContent({
  className,
  children,
  closeLabel,
  showCloseButton = true,
  dir: providedDir,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  closeLabel: string
  showCloseButton?: boolean
}) {
  // Portalled content is outside the app wrapper, so carry the document
  // direction onto the dialog itself just like the other overlay primitives.
  const documentDirection = useDocumentDirection()
  const dir = providedDir ?? documentDirection

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          // Centred by auto margins on both inline sides, so nothing here has
          // a physical side and Arabic needs no second rule.
          'fixed inset-x-0 top-1/2 z-50 mx-auto flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-y-1/2 flex-col gap-0 overflow-y-auto',
          // The modal elevation (DESIGN.md §4): overlay paper, 8px corners,
          // the modal shadow and no border, over the scrim.
          'rounded-lg bg-popover text-popover-foreground shadow-modal',
          'duration-[var(--motion-base)] data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          className,
        )}
        {...props}
        dir={dir}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className={cn(
              'absolute top-4 end-4 inline-flex size-control items-center justify-center rounded-sm [&_svg]:size-4',
              'text-muted-foreground transition-ui hover:bg-accent hover:text-foreground',
              'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
              'disabled:pointer-events-none',
            )}
          >
            <XIcon />
            <span className="sr-only">{closeLabel}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 px-6 pt-6 pb-4 pe-16 text-start', className)}
      {...props}
    />
  )
}

/**
 * Padded body for prose and form content. Under a header it drops its top
 * padding, so the header's 16px is the one gap. Tables opt out with `p-0`.
 * A body whose children all render nothing (an optional error) is hidden,
 * so it leaves no blank inset.
 */
function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-body"
      className={cn('p-6 empty:hidden [[data-slot=dialog-header]+&]:pt-0', className)}
      {...props}
    />
  )
}

/**
 * Actions at the inline end, over a `--border` rule: Cancel first, the
 * primary action last (DESIGN.md §5 "Buttons": one primary per group, always
 * last). The built-in close button, when shown, is the Cancel and is written
 * before the caller's actions for that reason. On a phone the buttons stack
 * at full width with the primary on top, where the thumb reaches it first.
 */
function DialogFooter({
  className,
  closeLabel,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  closeLabel?: string
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-col-reverse gap-2 border-t border-border px-6 py-4 sm:flex-row sm:flex-wrap sm:justify-end',
        className,
      )}
      {...props}
    >
      {showCloseButton && closeLabel ? (
        <DialogPrimitive.Close asChild>
          <Button variant="secondary">{closeLabel}</Button>
        </DialogPrimitive.Close>
      ) : null}
      {children}
    </div>
  )
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('max-w-[72ch] text-body text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
